import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpResponse, HttpStatusCode} from "@angular/common/http";
import {ActivatedRoute, Router} from '@angular/router';
import {BehaviorSubject, lastValueFrom} from 'rxjs';
import {filter, map} from "rxjs/operators";
import {AlertService} from "./alert.service";
import {InactivityService} from './inactivity.service';
import {LoggingService} from './logging.service';
import {StorageService} from './storage.service';
import {AppService} from './app.service';
import {ApiService} from './api.service';
import {NoticeService} from "./notice.service";
import {NavService} from './nav.service';
import {ToastService} from './toast.service';
import type {AuthUserInterface, KioskSignInResult, KioskUserInterface} from 'src/app/interfaces';
import {AppMode} from 'src/app/interfaces';

@Injectable({
  providedIn: 'root',
})
export class KioskService {
  readonly isKioskMode$ = this.storage.isKioskMode$;
  readonly kioskUsers$ = new BehaviorSubject<KioskUserInterface[]>([]);
  private pinInput: HTMLInputElement;

  constructor(
    private readonly alert: AlertService,
    private readonly inactivity: InactivityService,
    private readonly logger: LoggingService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly app: AppService,
    private readonly notice: NoticeService,
    private readonly storage: StorageService,
    private readonly api: ApiService,
    private readonly toast: ToastService,
    private readonly nav: NavService,
  ) {
    this.inactivity.ended$.subscribe(async () => await this.reset());

    // loading app from kiosk add user, after sign-in flow
    this.route.queryParamMap.pipe(
      map((paramMap) => paramMap.get('kiosk') === 'true'),
      filter((isKiosk) => isKiosk),
    ).subscribe(async () => {
      console.log('removing kiosk route param');
      this.ensureKioskMode();
      await this.router.navigate(['/home'], {replaceUrl: true});
    });

    this.app.userPresets$.pipe(
      filter(({data}) => data.length > 0 && this.app.appMode === AppMode.Kiosk),
    ).subscribe(() => {
      this.startTimer();
    });
  }

  startTimer(): void {
    this.inactivity.startTimer();
  }

  async loadUsers(): Promise<void> {
    const users = await lastValueFrom(
      this.api.getData<KioskUserInterface[]>(`auth/kiosk/users`, {isAnonymous: true}),
    ) as KioskUserInterface[];
    this.kioskUsers$.next(users);
  }

  ensureKioskMode(): void {
    if (this.storage.isAppMode$.value) {
      this.storage.appMode$.next(AppMode.Kiosk);
    }
  }

  async signIn(subjectId: string, pin: string): Promise<KioskSignInResult> {
    if (this.app.isOnline) {
      try {
        const response = await lastValueFrom(
          this.api.postData<{ subjectId: string; pin: string }, {
            user: AuthUserInterface,
            message: string
          }>(`auth/kiosk/sign-in`, {subjectId, pin}, false),
        );
        console.log('kiosk switchUser: ', response);
        await this.storage.setUser(response.user);
        // pops the pushed /switch-user when the sign-in page was opened from home, instead of
        // replacing it with a second home entry
        await this.nav.back();
        await this.toast.present(response.message, 1000, 'top');
        return 'signed-in';
      } catch (error) {
        this.logger.error('Kiosk sign-in error: ', error);
        // The list flags an expired session before the PIN is sent, but the list
        // can be stale, so the server's answer is handled too. The caller offers
        // the renewal; this alert would only duplicate it.
        if (error instanceof HttpErrorResponse && error.error?.code === 'kiosk_session_expired') {
          return 'expired';
        }
        if (error instanceof HttpErrorResponse && (error.status === HttpStatusCode.Unauthorized || error.status === HttpStatusCode.NotFound)) {
          await this.alert.present({header: 'Kiosk Sign-in', message: error.error.message});
        } else {
          await this.alert.present({
            header: 'Kiosk Sign-in',
            message: 'Oops, something went wrong signing in. Please contact support. Error: ' + JSON.stringify(error),
          });
        }
        return 'failed';
      }
    } else {
      await this.alert.present({
        header: 'Kiosk Sign-in',
        message: 'Oops, it appears you an not online. You must be online to sign in.',
      });
      return 'failed';
    }
  }

  async setPin(message: string, pin?: string): Promise<void> {
    if (this.app.isOnline) {
      await this.alert
        .present({
          header: 'User PIN',
          message,
          inputs: [
            {
              id: 'alert-user-pin',
              name: 'pin',
              type: 'tel',
              value: pin,
              placeholder: 'PIN',
              attributes: {
                oninput: this.onPinInput.bind(this),
                maxlength: 6,
              },
            },
          ],
          buttons: [
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Submit',
              handler: async (inputs?: { pin: string }) => {
                try {
                  await lastValueFrom(
                    this.api.postData<{ pin: string }, HttpResponse<void>>(`auth/kiosk/set-pin`, {pin: inputs.pin}),
                  );
                  await this.toast.present('User PIN set successfully.', 2000, 'middle');
                } catch (error) {
                  this.logger.error('Kiosk set pin error: ', error);
                  if (error instanceof HttpErrorResponse && error.status === 422) {
                    await this.alert.present({
                      header: 'User Pin',
                      message: 'PIN must be between 4 and 6 digits. Please check your entry and try again.',
                      buttons: [
                        {
                          text: 'Okay',
                          handler: () => {
                            this.setPin(message, inputs.pin).then();
                          },
                        },
                      ],
                    });
                  } else {
                    await this.alert.present({
                      header: 'User PIN',
                      message: 'PIN set failed. Please contact support. Error: ' + JSON.stringify(error),
                    });
                  }
                }
              },
            },
          ],
        });
      // set focus to input
      await new Promise<void>(resolve => setTimeout(resolve, 250));
      this.pinInput = document.getElementById('alert-user-pin') as HTMLInputElement;
      this.pinInput?.focus();
    } else {
      await this.alert.present({
        header: 'User PIN',
        message: 'Oops, it appears you an not online. You must be online to set your PIN.',
      });
    }
  }

  onPinInput(): void {
    this.pinInput.value = this.pinInput.value.replace(/[^0-9]/g, '');
  }

  // stopping the timer calls next on the end$ observable which then calls reset below.
  signOut(): void {
    this.inactivity.stopTimer();
  }

  async reset(): Promise<void> {
    await this.storage.removeUser(false);
    this.app.setEntity({entity: null, getLocal: true});
    this.app.setUserPresets(null);
    this.app.setLastPunch({
      clientId: null,
      action: 'Clock Out',
      status: 'undefined',
      label: 'Clock In/Out',
      bunit: null,
      projectId: null,
      in: null,
      out: null,
      closed: false,
      approved: false,
    });
    this.notice.setList(null);
    // from a pushed menu page, return to home first so the replace below lands on home's entry
    await this.nav.popToHome();
    await this.router.navigate(['/switch-user'], {replaceUrl: true});
    if (this.app.isOnline) {
      await lastValueFrom(
        this.api.postData<void, void>(`auth/kiosk/sign-out`, null, false),
      );
    } else {
      await this.alert.present({
        header: 'Kiosk Sign-out',
        message: 'Oops, it appears you are not online. You may not be signed out properly, which could cause problems with other users. Please contact support.',
      });
    }
  }

  async confirmRemoveUser(user: KioskUserInterface, onConfirmed: (user: KioskUserInterface) => Promise<void>): Promise<void> {
    await this.alert.present({
      header: 'Remove User',
      message: `Are you sure you want to remove this user (${user.resno ?? user.name}) from this device?</br>
                    <b>NOTE: This will not affect this users access on other devices.</b>`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Remove',
          handler: () => {
            onConfirmed(user);
          },
        },
      ],
      backdropDismiss: false,
    });
  }

  async removeUser(subjectId: string, pin?: string): Promise<boolean> {
    if (this.app.isOnline) {
      try {
        await lastValueFrom(
          this.api.postData<{ subjectId: string; pin?: string }, {
            user: AuthUserInterface,
            message: string
          }>(`auth/kiosk/remove-user`, {subjectId, pin}, false),
        );

        await this.app.removeUser(subjectId);
        return true;
      } catch (error) {
        this.logger.error('Error removing kiosk user: ', error);
        if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized) {
          await this.alert.present({
            header: 'Remove User',
            message: error.error.message,
          });
        } else {
          await this.alert.present({
            header: 'Remove User',
            message: 'Remove kiosk user failed. Please contact support. Error: ' + JSON.stringify(error),
          });
        }
        return false;
      }
    } else {
      await this.alert.present({
        header: 'Remove User',
        message: 'Oops, it appears you an not online. You must be online to remove a user.',
      });
    }
  }

  async confirmCopyUsers(): Promise<void> {
    if (this.app.isOnline) {
      let deviceId: string;
      let pin: string;
      const alert = await this.alert.present({
        header: 'Copy Kiosk Users',
        message: 'Enter the device id for the kiosk users you would like to copy, and the PIN of one user on that kiosk.',
        inputs: [
          {
            name: 'deviceId',
            type: 'text',
            placeholder: 'Device ID',
          },
          {
            name: 'pin',
            type: 'password',
            placeholder: 'PIN of a user on that kiosk',
            // Not 6 like a user's PIN: support can enter the master PIN here, which
            // is longer. The API caps it at 64.
            attributes: {inputmode: 'numeric', maxlength: 64},
          },
        ],
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Load',
            handler: (data) => {
              deviceId = data.deviceId;
              pin = data.pin;
            },
          },
        ],
        backdropDismiss: false,
      });
      await alert.onDidDismiss();
      if (deviceId) {
        await this.copyKioskUsers(deviceId, pin);
      }
    } else {
      await this.alert.present({
        header: 'Copy Kiosk Users',
        message: 'Oops, it appears you an not online. You must be online to copy kiosk users.',
      });
    }
  }

  async copyKioskUsers(deviceId: string, pin: string): Promise<void> {
    if (!!deviceId && !!pin) {
      const overlay = this.app.showOverlay('Coping kiosk users... please wait.');
      try {
        const response = await lastValueFrom(
          this.api.postData<{ deviceId: string; pin: string }, {
            copied: number,
            message: string
          }>(`auth/kiosk/copy-users`, {deviceId, pin}, false),
        );
        console.log('copy users response: ', response);
        await this.alert.present({header: 'Copy Kiosk Users', message: response.message});
      } catch (e) {
        // 401 is an unknown device or a wrong PIN, 422 a malformed entry and 429 too
        // many tries; each carries a message meant for the person at the kiosk.
        const expected = e instanceof HttpErrorResponse && [
          HttpStatusCode.Unauthorized,
          HttpStatusCode.UnprocessableEntity,
          HttpStatusCode.TooManyRequests,
        ].includes(e.status) && !!e.error?.message;
        await this.alert.present({
          header: 'Copy Kiosk Users',
          message: expected
            ? e.error.message
            : 'Oops, there was an error coping these users. Please contact support or try again. Error: ' +
            JSON.stringify(e),
        });
      } finally {
        overlay.then((o) => o.dismiss());
      }
    } else {
      await this.alert.present({
        header: 'Copy Kiosk Users',
        message: 'No device id or PIN supplied. Please enter the device id for the kiosk users you would like to copy, and the PIN of one of them.',
      });
    }
  }
}
